import type { TFunction } from 'i18next';
import * as yup from 'yup';

import { ProcessUpdateDtoAssessmentType } from '@/api/generated';
import { getTalentRegistrationFormValidationSchema } from '@/views/TalentRegistrationForm/schema';

import { AccessMethods, WebcamMode } from './SettingsTab/options';

export const getProcessSettingsBasicInfoValidation = (t: TFunction) =>
  yup.object().shape({
    name: yup.string().required(
      t('validation:mandatory', {
        field: t('settings:processSettings.form.basicInfo.processName')
      })
    ),
    assessmentType: yup
      .string()
      .oneOf(Object.values(ProcessUpdateDtoAssessmentType))
      .required(
        t('validation:mandatory', {
          field: t('settings:processSettings.form.basicInfo.assessmentType')
        })
      ),
    description: yup.string().optional(),
    availability: yup
      .object()
      .shape({
        start: yup.string().optional(),
        end: yup
          .string()
          .optional()
          .test('dates', t('validation:endDateAfterStart'), function (endDate) {
            const { start } = this.parent;
            if (!start || !endDate) return true;

            return new Date(endDate) > new Date(start);
          })
      })
      .default(undefined),
    status: yup.string().required(
      t('validation:mandatory', {
        field: t('settings:processSettings.form.basicInfo.availability')
      })
    )
  });

export const getProcessSettingsAccessInvitationValidation = (t: TFunction) =>
  yup.object().shape({
    talentInvitationLanguage: yup.string().required(
      t('validation:mandatory', {
        field: t('settings:processSettings.form.accessInvitation.talentInvitationLanguage')
      })
    ),
    invitationChannels: yup
      .array()
      .of(yup.string())
      .when('accessMethods', {
        is: (accessMethods: string[]) => accessMethods?.includes(AccessMethods.Link),
        then: schema =>
          schema
            .min(
              1,
              t('validation:atLeastOne', {
                field: t('settings:processSettings.form.accessInvitation.authMethods')
              })
            )
            .required(
              t('validation:mandatory', {
                field: t('settings:processSettings.form.accessInvitation.authMethods')
              })
            ),
        otherwise: schema => schema.nullable()
      }),

    accessMethods: yup
      .array()
      .of(yup.string())
      .min(
        1,
        t('validation:atLeastOne', {
          field: t('settings:processSettings.form.accessInvitation.testAccess')
        })
      )
      .required(
        t('validation:mandatory', {
          field: t('settings:processSettings.form.accessInvitation.testAccess')
        })
      )
  });

export const getProcessSettingsTalentRegistrationValidation = (t: TFunction) =>
  getTalentRegistrationFormValidationSchema(t);

export const getProcessSettingsTalentsValidation = (t: TFunction) =>
  yup.object().shape({
    talentsToAdd: yup
      .array(
        yup.string().required(
          t('validation:mandatory', {
            field: t('settings:processSettings.form.talents.addTalent')
          })
        )
      )
      .optional()
      .default([])
  });

export const getProcessSettingsTestPropertiesValidation = (t: TFunction) =>
  yup.object().shape({
    enableClarificationQuestions: yup.boolean().required(
      t('validation:mandatory', {
        field: t('settings:processSettings.form.testProperties.clarificationEnableQuestions')
      })
    ),
    webcam: yup.string().required(
      t('validation:mandatory', {
        field: t('settings:processSettings.form.testProperties.webcamTitle')
      })
    ),
    proctoringAI: yup
      .boolean()
      .required(
        t('validation:mandatory', {
          field: t('settings:processSettings.form.testProperties.webcamProctoringAI')
        })
      )
      .when('webcam', {
        is: (webcam: string) => webcam === WebcamMode.Disabled,
        then: schema =>
          schema.test(
            'proctoring-disabled-without-webcam',
            t('validation:proctoringRequiresWebcam', {
              field: t('settings:processSettings.form.testProperties.webcamProctoringAI')
            }),
            value => !value
          ),
        otherwise: schema => schema
      }),
    captureFrequency: yup
      .number()
      .min(
        1,
        t('validation:limits.minimum', {
          field: '',
          count: 1,
          unit: ''
        })
      )
      .max(
        600,
        t('validation:limits.maximum', {
          field: '',
          count: 600,
          unit: ''
        })
      )
      .required(
        t('validation:mandatory', {
          field: t('settings:processSettings.form.testProperties.webcamCaptureFrequency')
        })
      )

    // proctoringAIFrequency: yup
    //   .number()
    //   .min(
    //     30,
    //     t('validation:limits.minimum', {
    //       field: '',
    //       count: 30,
    //       unit: ''
    //     })
    //   )
    //   .max(
    //     120,
    //     t('validation:limits.maximum', {
    //       field: '',
    //       count: 120,
    //       unit: ''
    //     })
    //   )
    //   .required(
    //     t('validation:mandatory', {
    //       field: t('settings:processSettings.form.testProperties.webcamProctoringAIFrequency')
    //     })
    //   )
    // testType: yup.string().required(
    //   t('validation:mandatory', {
    //     field: t('settings:processSettings.form.testProperties.normsTestType')
    //   })
    // ),
    // primaryLabel: yup.string().required(
    //   t('validation:mandatory', {
    //     field: t('settings:processSettings.form.testProperties.normsPrimaryLabel')
    //   })
    // )
    // customizations: yup.array().of(
    //   yup.object().shape({
    //     test: yup.string().required(
    //       t('validation:mandatory', {
    //         field: t('settings:processSettings.form.testProperties.normsTestTypeName')
    //       })
    //     ),
    //     normTypes: yup.string().required(
    //       t('validation:mandatory', {
    //         field: t('settings:processSettings.form.testProperties.normsChoosePrimaryType')
    //       })
    //     )
    //   })
    // )
  });
